import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
} from '@mui/icons-material';
import { adminApi, Organization } from '../services/api';

interface MemberInfo {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  isPaid: boolean;
  isEnabled: boolean;
  status: string;
  createdAt: string;
}

const ORG_STATUSES = ['active', 'inactive', 'trial'];

const OrganizationDetailPage: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [members, setMembers] = useState<MemberInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState<string | null>(null);

  // Edit-org dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editStatus, setEditStatus] = useState('active');
  const [editDomains, setEditDomains] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Assign-users dialog
  const [assignOpen, setAssignOpen] = useState(false);
  const [candidates, setCandidates] = useState<MemberInfo[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidateFilter, setCandidateFilter] = useState('');
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);

  // Delete-org
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [removingId, setRemovingId] = useState<string | null>(null);

  const displayName = organization
    ? organization.branding?.displayName || organization.displayName || organization.name
    : '';

  const fetchOrg = useCallback(async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      const res = await adminApi.getOrganization(orgId);
      if (res.success) setOrganization(res.data.organization);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load organization');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  const fetchMembers = useCallback(async () => {
    if (!orgId) return;
    try {
      setMembersLoading(true);
      setMembersError(null);
      const res = await adminApi.listOrganizationMembers(orgId);
      if (res.success) setMembers(res.data.members || []);
    } catch (err: any) {
      setMembersError(err.response?.data?.message || err.message || 'Failed to load members');
    } finally {
      setMembersLoading(false);
    }
  }, [orgId]);

  useEffect(() => { fetchOrg(); }, [fetchOrg]);
  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const openEdit = () => {
    if (!organization) return;
    setEditDisplayName(displayName);
    setEditStatus(organization.status || 'active');
    setEditDomains((organization.domains || []).join(', '));
    setEditError(null);
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!orgId) return;
    try {
      setSavingEdit(true);
      setEditError(null);
      await adminApi.updateOrganization(orgId, {
        displayName: editDisplayName.trim() || undefined,
        status: editStatus,
        domains: editDomains.split(',').map((d) => d.trim()).filter(Boolean),
      });
      await fetchOrg();
      setEditOpen(false);
    } catch (err: any) {
      setEditError(err.response?.data?.message || err.message || 'Failed to update organization');
    } finally {
      setSavingEdit(false);
    }
  };

  const openAssign = async () => {
    setAssignOpen(true);
    setAssignError(null);
    setCandidateFilter('');
    try {
      setCandidatesLoading(true);
      const res = await adminApi.listUsers({ limit: 60 });
      if (res.success) {
        const memberIds = new Set(members.map((m) => m.userId));
        setCandidates((res.data.users || []).filter((u: MemberInfo) => !memberIds.has(u.userId)));
      }
    } catch (err: any) {
      setAssignError(err.response?.data?.message || err.message || 'Failed to load users');
    } finally {
      setCandidatesLoading(false);
    }
  };

  const handleAssign = async (userId: string) => {
    if (!orgId) return;
    try {
      setAssigningId(userId);
      setAssignError(null);
      await adminApi.updateUser(userId, { organizationId: orgId });
      setCandidates((prev) => prev.filter((c) => c.userId !== userId));
      await fetchMembers();
    } catch (err: any) {
      setAssignError(err.response?.data?.message || err.message || 'Failed to assign user');
    } finally {
      setAssigningId(null);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!orgId) return;
    try {
      setRemovingId(userId);
      setMembersError(null);
      await adminApi.updateUser(userId, { organizationId: '' });
      await fetchMembers();
    } catch (err: any) {
      setMembersError(err.response?.data?.message || err.message || 'Failed to remove member');
    } finally {
      setRemovingId(null);
    }
  };

  const handleDeleteOrg = async () => {
    if (!orgId) return;
    try {
      setDeleting(true);
      setDeleteError(null);
      await adminApi.deleteOrganization(orgId);
      navigate('/organizations');
    } catch (err: any) {
      setDeleteError(err.response?.data?.message || err.message || 'Failed to delete organization');
      setDeleting(false);
    }
  };

  const filteredCandidates = candidates.filter((c) => {
    const q = candidateFilter.trim().toLowerCase();
    if (!q) return true;
    return (
      c.email.toLowerCase().includes(q) ||
      `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !organization) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/organizations')} sx={{ mb: 2 }}>
          Back to Organizations
        </Button>
        <Alert severity={error ? 'error' : 'warning'}>{error || 'Organization not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/organizations')} sx={{ mb: 3 }}>
        Back to Organizations
      </Button>

      {/* Organization info */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" fontWeight={700}>{displayName}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={openEdit}>
                Edit
              </Button>
              <Button variant="outlined" size="small" color="error" startIcon={<DeleteIcon />} onClick={() => { setDeleteError(null); setDeleteOpen(true); }}>
                Delete
              </Button>
            </Box>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Organization ID</Typography>
              <Typography sx={{ fontFamily: 'monospace' }}>{organization.organizationId}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Name</Typography>
              <Typography>{organization.name}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={organization.status || 'active'}
                  size="small"
                  color={organization.status === 'inactive' ? 'default' : organization.status === 'trial' ? 'warning' : 'success'}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">Domains</Typography>
              <Typography>{(organization.domains || []).join(', ') || '-'}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Members */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h6" fontWeight={600}>Members</Typography>
              <Chip label={`${members.length} ${members.length === 1 ? 'user' : 'users'}`} size="small" />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" startIcon={<RefreshIcon />} onClick={fetchMembers} disabled={membersLoading}>
                Refresh
              </Button>
              <Button size="small" variant="contained" startIcon={<PersonAddIcon />} onClick={openAssign}>
                Assign Users
              </Button>
            </Box>
          </Box>

          {membersError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMembersError(null)}>{membersError}</Alert>
          )}

          {membersLoading && members.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : members.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Paid</TableCell>
                    <TableCell>Joined</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members.map((member) => (
                    <TableRow key={member.userId} hover>
                      <TableCell
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/users/${member.userId}`)}
                      >
                        {member.email}
                      </TableCell>
                      <TableCell>
                        {member.firstName || member.lastName
                          ? `${member.firstName || ''} ${member.lastName || ''}`.trim()
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip label={member.isEnabled ? 'Active' : 'Disabled'} size="small" color={member.isEnabled ? 'success' : 'error'} />
                      </TableCell>
                      <TableCell>
                        <Chip label={member.isPaid ? 'Yes' : 'No'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{member.createdAt ? new Date(member.createdAt).toLocaleDateString() : '-'}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          title="Remove from organization"
                          aria-label="Remove from organization"
                          disabled={removingId === member.userId}
                          onClick={() => handleRemoveMember(member.userId)}
                        >
                          {removingId === member.userId ? <CircularProgress size={16} /> : <PersonRemoveIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary">No members yet — use “Assign Users” to add some.</Typography>
          )}
        </CardContent>
      </Card>

      {/* Edit-org dialog */}
      <Dialog open={editOpen} onClose={() => !savingEdit && setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Organization</DialogTitle>
        <DialogContent>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Display Name" value={editDisplayName} onChange={(e) => setEditDisplayName(e.target.value)} fullWidth />
            <TextField label="Status" select value={editStatus} onChange={(e) => setEditStatus(e.target.value)} fullWidth>
              {ORG_STATUSES.map((s) => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
            </TextField>
            <TextField
              label="Domains"
              value={editDomains}
              onChange={(e) => setEditDomains(e.target.value)}
              fullWidth
              placeholder="acme.com, acme.co.za"
              helperText="Comma-separated email domains (optional)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={savingEdit}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={savingEdit} startIcon={savingEdit ? <CircularProgress size={20} /> : null}>
            {savingEdit ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign-users dialog */}
      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Users to {displayName}</DialogTitle>
        <DialogContent>
          {assignError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAssignError(null)}>{assignError}</Alert>}
          <TextField
            label="Filter users"
            value={candidateFilter}
            onChange={(e) => setCandidateFilter(e.target.value)}
            fullWidth
            size="small"
            sx={{ mt: 1, mb: 1 }}
            placeholder="Search by email or name"
          />
          {candidatesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          ) : filteredCandidates.length > 0 ? (
            <List dense sx={{ maxHeight: 360, overflow: 'auto' }}>
              {filteredCandidates.map((u) => (
                <ListItem
                  key={u.userId}
                  secondaryAction={
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={assigningId === u.userId ? <CircularProgress size={14} /> : <PersonAddIcon />}
                      disabled={assigningId === u.userId}
                      onClick={() => handleAssign(u.userId)}
                    >
                      Add
                    </Button>
                  }
                >
                  <ListItemText
                    primary={u.email}
                    secondary={`${u.firstName || ''} ${u.lastName || ''}`.trim() || undefined}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              {candidates.length === 0 ? 'All loaded users are already members.' : 'No users match the filter.'}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            Shows the first 60 users. Assigning sets the user's organization.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Delete-org dialog */}
      <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete organization?</DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
          <Typography>
            This permanently deletes <strong>{displayName}</strong>. The backend refuses if the
            org still has members — remove them first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteOrg} disabled={deleting} startIcon={deleting ? <CircularProgress size={20} /> : null}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizationDetailPage;
